// The enums of the claims domain that travel in the contracts: @nexxt/db builds the Postgres enums from these tuples.

/**
 * `claims.claim_type` (B-55, spec v0.2 area N): parcels missing, products defective (to be replaced) and products
 * faulty. Any other kind the Customer Service names is `other`, with what the customer wrote.
 */
export const claimTypes = ["missing_parcels", "defective", "faulty", "other"] as const;
export type ClaimType = (typeof claimTypes)[number];

/** The types that need a photo, at least one (B-55): what is wrong with a product is seen. */
export const claimTypesWithPhotos: readonly ClaimType[] = ["defective", "faulty"];

/**
 * `claims.claim_status` (ADR-225): `open` until Zendesk has its ticket (I-17), `submitted` once it has; then what the
 * ticket says (I-32): `in_progress` while the Customer Service works it, `resolved` once solved, `closed`.
 */
export const claimStatuses = ["open", "submitted", "in_progress", "resolved", "closed"] as const;
export type ClaimStatus = (typeof claimStatuses)[number];

/**
 * `claims.claim_outcome` (B-55, T-67, ADR-225): what the Customer Service decided on Zendesk. The replacement order
 * and the credit note are made in Nexxt (B-75).
 */
export const claimOutcomes = ["accepted", "rejected", "replacement", "credit_note"] as const;
export type ClaimOutcome = (typeof claimOutcomes)[number];

/**
 * `claims.ticket_requester` (B-78, B-98, ADR-252): who the ticket of a claim was asked for on Zendesk, who gets its
 * answers. The customer who opened it in Frontrow; for a claim the Customer Service opened, the buyer of the order,
 * otherwise a `buyer` contact of the customer, otherwise the Customer Service itself, which then sees a warning.
 */
export const ticketRequesters = ["customer", "order_buyer", "buyer_contact", "customer_service"] as const;
export type TicketRequester = (typeof ticketRequesters)[number];

/** The folder of `platform.folder` the photos and files of a claim are uploaded into (B-55, ADR-225). */
export const CLAIMS_FOLDER = "claims";

/** Files a claim has at most (B-55): JPEG, PNG or PDF, 10 MB each, which the folder says. */
export const MAX_CLAIM_FILES = 10;

/**
 * What the folder `claims` takes (B-55, migration 0117), for the forms of the apps to say it before the upload
 * (ADR-228): files checks it again, with the first bytes of the content.
 */
export const CLAIM_FILE_EXTENSIONS: readonly string[] = ["jpg", "jpeg", "png", "pdf"];
export const CLAIM_FILE_MAX_MB = 10;

/** Why the ticket of a claim could not be opened (I-17 refused, or given up): the Customer Service sends it again. */
export interface ClaimTicketError {
  cause: "rejected" | "failed" | "dead";
  /** The first error the system gave, when it gave one. */
  message: string | null;
}
